import { ChangeEvent, JSX, useEffect, useLayoutEffect, useRef, useState } from "react";
import { styled, useTheme } from "styled-components";
import { Box, createCSSFromObject, HBox, VBox } from "../box/Box";
import { InfoButton } from "../info/InfoButton";
import { Select } from "../select/Select";
import Spacer from "../spacer/Spacer";
import { Typo, TypoCaption } from "../text/Typo";
import { ITheme, RecursiveCSS } from "../theme/GlobalTheme";

export const InputBoxStyle = (disable: boolean, readOnly?: boolean) => {
	return {
		padding: "0px",
		border: disable ? "1px solid rgba(0,0,0,.1)" : "1px solid inputBorderPrimary",
		cursor: readOnly || disable ? "default" : "text",
		borderRadius: "4px",
		backgroundColor: disable ? "rgba(0,0,0,.1)" : "inputBackgroundPrimary",
		transition: "border .2s",
		"&:hover": {
			border: disable ? "1px solid rgba(0,0,0,.1)" : "1px solid inputBorderSecondary",
			transition: "border .2s",
		},
	};
};

export const InputFontStyle = (size: "small" | "medium" | "large") => {
	return {
		outline: "0",
		margin: "0",
		padding: size === "small" ? "8px 12px" : size === "large" ? "16px 20px" : "12px 16px",
		fontSize: size === "small" ? "13px" : size === "large" ? "1.2rem" : "1rem",
		lineHeight: "1.25",
		height: size === "small" ? "34px" : size === "large" ? "56px" : "44px",
		boxSizing: "border-box" as const,
	};
};

export interface IInputProps {
	sx?: RecursiveCSS;
	isCard?: boolean;
	sxInput?: RecursiveCSS;
	name: string;
	label?: string;
	iconRight?: JSX.Element;
	iconLeft?: JSX.Element;
	placeholder?: string;
	data: { [key: string]: string | number | boolean };
	onChange?: (value: string) => void;
	onFocusProps?: () => void;
	onBloorProps?: () => void;
	size?: "small" | "medium" | "large";
	subtitle?: string;
	type?: "switchbox" | "text" | "password" | "phone" | "textarea" | "date" | "search" | "dateInput" | "number";
	switchboxLabels?: { yes: string; no: string };
	inputColor?: string;
	disabled?: boolean;
	readOnly?: boolean;
	multiline?: boolean;
	maxLength?: number;
	description?: string;
	units?: string;
	mandatory?: boolean;
	id?: string;
	refvalue?: string | null;
	validator?: (val: string) => boolean;
	formatAmount?: boolean;
	asPercent?: boolean;
	variant?: "default" | "reference";
}

const TextAreaEl = styled.textarea<{ $size?: string; $color?: string }>`
	width: calc(100%);
	display: block;
	border: none;
	padding: 0;
	outline: 0;
	margin: 0;
	height: auto;
	padding: 12px 16px;
	font-size: 0.87rem;
	box-sizing: border-box;
	background-color: transparent;
	${props => (props.$size ? `font-size:${props.$size};` : ``)};
	${props => (props.$color ? `color:${props.$color};` : ``)};
	resize: none;
	&::placeholder {
		font-style: italic;
	}
	&:focus {
		background-color: transparent;
	}
	&:disabled {
	}
`;

// 0 0 16 10
//!TODO: replace with icon component
const eyeOpened = (
	<svg width="14" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8 0C1.3 0 0 5.1 0 5.1C0 5.1 2.2 9.2 7.9 9.2C13.6 9.2 16 5.2 16 5.2C16 5.2 14.7 0 8 0ZM5.3 1.5C5.8 1.2 6.6 1.2 6.6 1.2C6.6 1.2 6.1 2.1 6.1 2.8C6.1 3.5 6.3 3.9 6.3 3.9L5.2 4.1C5.2 4.1 4.9 3.6 4.9 2.9C4.9 2.1 5.3 1.5 5.3 1.5ZM7.9 8.2C3.8 8.2 1.7 5.9 1.1 5C1.4 4.3 2.2 2.8 4.2 1.8C4.1 2.2 4 2.6 4 3.1C4 5.3 5.8 7.1 8 7.1C10.2 7.1 12 5.3 12 3.1C12 2.6 11.9 2.2 11.8 1.8C13.8 2.7 14.6 4.3 14.9 5C14.2 5.9 12.1 8.2 7.9 8.2Z"
			fill="#1C2E45"
			fillOpacity="0.6"
		/>
	</svg>
);

//!TODO: replace with icon component
const eyeClosed = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M16.9 9.19995L16.1 9.99995C17.8 10.9 18.6 12.3 18.9 13C18.2 13.9 16.1 16.1 11.9 16.1C11.2 16.1 10.7 16 10.1 15.9L9.29999 16.7C10.1 17 11 17.1 11.9 17.1C17.6 17.1 20 13.1 20 13.1C20 13.1 19.4 10.7 16.9 9.19995Z"
			fill="#1C2E45"
			fillOpacity="0.6"
		/>
		<path d="M16 11.1C16 10.8 16 10.5 15.9 10.3L11.1 15C11.4 15 11.7 15.1 12 15.1C14.2 15.1 16 13.3 16 11.1Z" fill="#1C2E45" fillOpacity="0.6" />
		<path
			d="M19.3 4L14.9 8.4C14.1 8.2 13.1 8 12 8C5.3 8 4 13.1 4 13.1C4 13.1 5 14.9 7.3 16.1L4 19.3V20H4.7L20 4.7V4H19.3V4ZM8 15.3C6.4 14.6 5.5 13.5 5.1 13C5.4 12.3 6.2 10.8 8.2 9.8C8.1 10.2 8 10.6 8 11.1C8 12.2 8.5 13.3 9.3 14L8 15.3ZM10.2 11.9L9.2 12.1C9.2 12.1 8.9 11.6 8.9 10.9C8.9 10.1 9.3 9.4 9.3 9.4C9.8 9.1 10.6 9.1 10.6 9.1C10.6 9.1 10.1 10 10.1 10.8C10 11.5 10.2 11.9 10.2 11.9Z"
			fill="#1C2E45"
			fillOpacity="0.6"
		/>
	</svg>
);

export const InputEl = styled.input<{
	$color?: string;
	$sx?: RecursiveCSS;
	$readOnly?: boolean;
	$size: "small" | "medium" | "large";
	$variant?: "default" | "reference";
}>`
	width: calc(100%);
	display: block;
	border: none;
	box-sizing: border-box;
	background-color: transparent;

	${props => createCSSFromObject(InputFontStyle(props.$size))}
	${props => (props.$color ? `color:${props.$color};` : ``)};
	${props => createCSSFromObject(props.$sx ?? {})}
	${props => props.$readOnly && `pointer-events: none; cursor: default;`}

	&::placeholder {
		font-style: ${props => (props.$variant === "reference" ? "normal" : "italic")};
		opacity: ${props => (props.$variant === "reference" ? "1" : "0.7")};
		color: ${props => (props.$variant === "reference" ? "#9a918a" : "inherit")};
		letter-spacing: ${props => (props.$variant === "reference" ? "0.08em" : "inherit")};
		text-transform: ${props => (props.$variant === "reference" ? "uppercase" : "inherit")};
	}
	&:focus {
		/* background-color: transparent; */
		${props => props.$sx?.backgroundColor ?? "background-color: transparent;"}
	}
	&:disabled {
	}
`;

const Input = ({
	multiline,
	disabled,
	readOnly,
	subtitle,
	type,
	label,
	data,
	name,
	iconRight,
	iconLeft,
	placeholder,
	onChange,
	sx,
	sxInput,
	size,
	inputColor,
	maxLength,
	isCard,
	onFocusProps,
	onBloorProps,
	description,
	units,
	mandatory,
	id,
	refvalue,
	validator,
	formatAmount,
	switchboxLabels,
	asPercent,
	variant = "default",
}: IInputProps) => {
	const theme = useTheme() as ITheme;
	const [value, setValue] = useState<string>("");
	const [_valid, setValid] = useState<boolean>(true);
	const [decoration, setDecoration] = useState<"valid" | "unvalid" | "neutral" | undefined>();
	const [focused, setFocused] = useState<boolean>(false);
	const [showPasswd, setShowPasswd] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const pendingSelectionRef = useRef<number | null>(null);

	if (!size) size = "medium";

	const applyAmountFormat = (val: string): string => {
		const clean = val.replace(/[^\d.]/g, "");
		if (!clean) return "";
		const parts = clean.split(".");
		const intRaw = parts[0].replace(/^0+(\d)/, "$1");
		const intPart = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
		if (parts.length > 1) {
			return intPart + "." + parts[1].slice(0, 2);
		}
		return intPart;
	};

	useEffect(() => {
		const raw = data[name] + "";
		setValue(formatAmount ? (focused ? applyAmountFormat(raw) : addDecimalPlaces(applyAmountFormat(raw))) : raw);
	}, [data, focused]);

	const addDecimalPlaces = (val: string): string => {
		if (val === "") return "";
		if (val.indexOf(".") === -1) {
			return val + ".00";
		} else {
			const parts = val.split(".");
			if (parts[1].length === 0) {
				return val + "00";
			} else if (parts[1].length === 1) {
				return val + "0";
			}
		}
		return val;
	};

	const getAmountSelectionIndex = (formattedValue: string, charsBeforeCaret: number) => {
		if (charsBeforeCaret <= 0) return 0;
		let seen = 0;
		for (let index = 0; index < formattedValue.length; index++) {
			if (/\d|\./.test(formattedValue[index])) seen++;
			if (seen >= charsBeforeCaret) return index + 1;
		}
		return formattedValue.length;
	};

	useLayoutEffect(() => {
		if (pendingSelectionRef.current === null || document.activeElement !== inputRef.current) return;
		const selection = pendingSelectionRef.current;
		pendingSelectionRef.current = null;
		inputRef.current?.setSelectionRange(selection, selection);
	}, [value]);

	const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		data[name] = e.target.value;

		if (type === "switchbox") {
			data[name] = e.target.value === "true" ? true : false;
		}

		if (finalType === "date") {
			if (!e.target.value) {
				data[name] = "";
				if (onChange) onChange("");
				setValue("");
				return;
			}

			// create date from yyyy-mm-dd
			const dateParts = e.target.value.split("-");
			const dateObj = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
			if (Number.isNaN(dateObj.getTime())) {
				data[name] = "";
				if (onChange) onChange("");
				setValue("");
				return;
			}

			data[name] = dateObj.getFullYear() + "-" + String(dateObj.getMonth() + 1).padStart(2, "0") + "-" + String(dateObj.getDate()).padStart(2, "0");
			if (onChange) onChange(data[name]);
			setValue(data[name]);
		} else if (type === "dateInput") {
			// Форматирование MM/YY для мобильных браузеров
			const formattedValue = formatDateInputValue(e.target.value, value);
			data[name] = formattedValue;
			callValidator(formattedValue);
			if (onChange) onChange(formattedValue);
			setValue(formattedValue);
		} else if (formatAmount) {
			const inputValue = e.target.value.replace(/,/g, ".");
			const selectionStart = e.target.selectionStart ?? inputValue.length;
			const charsBeforeCaret = inputValue.slice(0, selectionStart).replace(/\s/g, "").replace(/[^\d.]/g, "").length;
			const raw = inputValue.replace(/\s/g, "");
			if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
			const formattedValue = applyAmountFormat(raw);
			pendingSelectionRef.current = getAmountSelectionIndex(formattedValue, charsBeforeCaret);
			data[name] = raw;
			callValidator(raw);
			if (onChange) onChange(raw);
			setValue(formattedValue);
		} else if (asPercent) {
			let raw = e.target.value.replace(/[^\d.]/g, "");
			if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
			raw = raw.replace(/^0+(\d)/, "$1");
			const num = parseFloat(raw);
			if (!isNaN(num) && num > 100) return;
			data[name] = raw;
			callValidator(raw);
			if (onChange) onChange(raw);
			setValue(raw);
		} else {
			callValidator(e.target.value);
			if (onChange) onChange(e.target.value);
			const filteredValue = isCard
				? e.target.value
						.replace(/[^\dA-Z]/g, "")
						.replace(/(.{4})/g, "$1 ")
						.trim()
				: e.target.value;

			setValue(filteredValue);
		}
	};

	const callValidator = (val: string) => {
		if (refvalue !== undefined && validator) {
			const valid = validator(val);
			if (Number(refvalue) === Number(val)) {
				setDecoration("neutral");
			} else {
				valid ? setDecoration("valid") : setDecoration("unvalid");
			}
			setValid(valid);
		}
	};

	const onFocus = () => {
		onFocusProps && onFocusProps();
		setFocused(true);
	};

	const onBlur = () => {
		onBloorProps && onBloorProps();
		setFocused(false);
		if (formatAmount) {
			setValue(addDecimalPlaces(value));
		}
	};

	// Форматирование MM/YY через onChange (работает на мобильных браузерах)
	const formatDateInputValue = (rawValue: string, prevValue: string): string => {
		const delimiter = "/";
		const yearMin = new Date().getFullYear() - 2000;
		const yearMax = new Date().getFullYear() - 2000 + 25;

		// Определяем, удаляет ли пользователь символы
		const isDeleting = rawValue.length < prevValue.length;

		// Оставляем только цифры
		const digits = rawValue.replace(/\D/g, "");

		if (digits.length === 0) return "";

		let month = "";
		let year = "";

		// Обработка месяца (первые 1-2 цифры)
		if (digits.length >= 1) {
			const firstDigit = parseInt(digits[0], 10);

			if (digits.length === 1) {
				// Если первая цифра > 1, добавляем ведущий 0 (только при добавлении)
				if (firstDigit > 1 && !isDeleting) {
					month = "0" + digits[0];
				} else {
					month = digits[0];
				}
			} else {
				// Две цифры для месяца
				let monthNum = parseInt(digits.substring(0, 2), 10);

				// Валидация месяца 01-12
				if (monthNum === 0) {
					monthNum = 1;
				} else if (monthNum > 12) {
					monthNum = 12;
				}

				month = monthNum.toString().padStart(2, "0");
			}
		}

		// Обработка года (следующие 1-2 цифры после месяца)
		if (digits.length > 2) {
			const yearDigits = digits.substring(2, 4);

			if (yearDigits.length === 2) {
				let yearNum = parseInt(yearDigits, 10);

				// Валидация года в пределах yearMin-yearMax
				if (yearNum < yearMin) {
					yearNum = yearMin;
				} else if (yearNum > yearMax) {
					yearNum = yearMax;
				}

				year = yearNum.toString().padStart(2, "0");
			} else {
				year = yearDigits;
			}
		}

		// Формируем результат
		if (month.length === 2 && year.length > 0) {
			return month + delimiter + year;
		} else if (month.length === 2 && !isDeleting) {
			// Добавляем разделитель только при вводе, не при удалении
			return month + delimiter;
		}

		return month;
	};

	if (!inputColor) inputColor = theme.colors.textPrimary;

	let switchbox_no = "No";
	let switchbox_yes = "Yes";

	if (switchboxLabels) {
		switchbox_no = switchboxLabels.no;
		switchbox_yes = switchboxLabels.yes;
	}

	if (!iconRight && type === "password")
		iconRight = (
			<Box
				onClick={() => {
					setShowPasswd(!showPasswd);
				}}
				sx={{
					padding: "5px 15px 5px 5px",
					cursor: "pointer",
					width: "40px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}>
				{!showPasswd ? eyeOpened : eyeClosed}
			</Box>
		);

	let inputElement = null;
	if (multiline || type === "textarea")
		inputElement = (
			<TextAreaEl
				maxLength={maxLength ? maxLength : undefined}
				disabled={disabled}
				readOnly={readOnly}
				$color={inputColor}
				onFocus={onFocus}
				onBlur={onBlur}
				placeholder={placeholder}
				$size={size}
				onChange={handleOnChange}
				value={value}
			/>
		);

	if (type === "switchbox") {
		inputElement = (
			<Box
				onClick={() => {
					handleOnChange({
						target: {
							value: value === "true" ? "false" : "true",
						},
					} as any);
				}}
				sx={{
					width: "100%",
					minWidth: "50px",
					position: "relative",
					cursor: disabled ? "default" : "pointer",
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					alignItems: "center",
					...InputFontStyle(size),
					paddingLeft: "0px",
					paddingRight: "0px",
					pointerEvents: disabled ? "none" : "auto",
					"&>div": {
						paddingLeft: "10px",
						paddingRight: "10px",
						fontSize: "0.86em",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					},
					"&>div:nth-child(1)": {
						color: value === "false" ? "#af0c0c" : "#a49c9c",
					},
					"&>div:nth-child(2)": {
						color: value === "false" ? "#a49c9c" : "#1c3a72",
					},
				}}>
				<div>{switchbox_no}</div>
				<div>{switchbox_yes}</div>
				<Box
					sx={{
						position: "absolute",
						width: "calc(50% - 10px)",
						backgroundColor: "#193650",
						height: "2px",
						bottom: "3px",
						left: "5px",
						transition: "transform .2s",
						transform: value === "true" ? "translateX(calc(100% + 10px))" : "translateX(0)",
					}}></Box>
			</Box>
		);
	}

	let finalType = type;
	if (type === "password" && showPasswd) finalType = "text";

	let dateValue = "";
	if (type === "date") {
		if ((value as any) instanceof Date) {
			dateValue =
				(value as any).getFullYear() +
				"-" +
				String((value as any).getMonth() + 1).padStart(2, "0") +
				"-" +
				String((value as any).getDate()).padStart(2, "0");
		} else {
			// check to date
			try {
				let val: any;
				if (value === parseInt(value as any) + "") {
					val = new Date(parseInt(value as any));
				} else {
					val = new Date(value);
				}

				val = val.getFullYear() + "-" + String(val.getMonth() + 1).padStart(2, "0") + "-" + String(val.getDate()).padStart(2, "0");
				dateValue = val;
			} catch (e) {}
		}
	}

	if (!inputElement)
		inputElement = (
			<InputEl
				ref={inputRef}
				//id={id ? "value_" + id : undefined}
				maxLength={maxLength ? maxLength : undefined}
				disabled={disabled}
				$readOnly={readOnly}
				$sx={sxInput}
				$color={inputColor}
				$variant={variant}
				type={finalType}
				onFocus={onFocus}
				onBlur={onBlur}
				name={name}
				placeholder={placeholder}
				$size={size}
				onChange={handleOnChange}
				value={finalType === "date" ? dateValue : value}
			/>
		);

	let predefinedSelector = null;

	if (type === "phone") {
		predefinedSelector = (
			<Select
				values={[
					{ name: "Mobile", value: "mobile" },
					{ name: "Mobile", value: "mobile" },
					{ name: "Mobile", value: "mobile" },
					{ name: "Mobile", value: "mobile" },
				]}
			/>
		);
	}

	const getBackColor = (val: "valid" | "unvalid" | "neutral" | undefined) => {
		switch (val) {
			case "unvalid":
				return "#ff000028";

			case "valid":
				return "#33ff0028";

			default:
				return theme.colors.panelBackgroundPrimary;
			// return "unset";
		}
	};

	const inputFieldContent = (
		<Box
			id={id ? "control_" + id : undefined}
			sx={{
				flexGrow: 1,
				boxSizing: "border-box",
				overflow: "hidden",
				display: "flex",

				...InputBoxStyle(disabled ?? false, readOnly ?? false),
				backgroundColor: getBackColor(decoration),

				"&:hover": {
					//backgroundColor:"#e6e3dd", //F0EEEA
					border: disabled || readOnly ? undefined : "1px solid #1a5bff",
					transition: "border .2s",
				},

				"@media (prefers-color-scheme: dark)": {
					//backgroundColor:"#29272f",
				},

				...(focused ? { border: "1px solid #1a5bff" } : {}),
				...(variant === "reference"
					? {
							height: "30px",
							minHeight: "30px",
							border: focused ? "1px solid #bfaea3" : "1px solid #d8cfc8",
							borderRadius: "8px",
							backgroundColor: "#fbfaf8",
							boxShadow: "inset 0 1px 0 rgba(255,255,255,.75)",
							"&:hover": {
								border: "1px solid #bfaea3",
								backgroundColor: "#fff",
							},
						  }
					: {}),
			}}>
			{iconLeft}
			{inputElement}
			{units && (
				<>
					<VBox
						sx={{
							alignItems: "center",
							justifyContent: "center",
						}}>
						<Typo variant="caption">{units}</Typo>
					</VBox>
					<Spacer size="10px" />
				</>
			)}
			{iconRight}
			{refvalue !== undefined && (
				<Box
					sx={{
						width: "35%",
						flexShrink: "0",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "#c9c9c9",
						display: "flex",
						padding: InputFontStyle(size).padding,
					}}>
					<HBox sx={{ alignItems: "baseline" }} gap="4px">
						<Typo
							sx={{
								fontSize: InputFontStyle(size).fontSize,
								color: "#000000",
							}}
							variant="caption">
							{refvalue}
						</Typo>
						<Typo variant="caption" color="#000000">
							{units}
						</Typo>
					</HBox>
				</Box>
			)}
		</Box>
	);

	return (
		<Box
			// id={id ? "container_" + id : undefined}
			sx={{
				display: "flex",
				position: "relative",
				flexDirection: "column",
				gap: "3px",
				...sx,
			}}>
			<HBox sx={{ alignItems: "center", gap: "10px" }}>
				{label && (
					<TypoCaption>
						{label}
						{mandatory !== undefined && mandatory === true ? " *" : ""}:
					</TypoCaption>
				)}
				{description && <InfoButton text={description} />}
			</HBox>

			{predefinedSelector ? (
				<HBox gap="5px">
					{predefinedSelector}
					{inputFieldContent}
				</HBox>
			) : (
				inputFieldContent
			)}

			{subtitle && (
				<Typo variant="small" sx={{ opacity: 0.7 }}>
					{subtitle}
				</Typo>
			)}
		</Box>
	);
};

export default Input;
